<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { QuizzesClient } from "@/services/QuizzesClient";
import { QuestionsClient } from "@/services/QuestionsClient";
import { CoursesClient } from "@/services/CoursesClient";
import { ChaptersClient } from "@/services/ChaptersClient";
import { ResultsClient } from "../../services/ResultsClient";
import { StudentsClient } from "@/services/StudentsClient";
import confetti from 'canvas-confetti';
import Swal from "sweetalert2";
import ChapterPage from "@/views/student/components/ChapterPage.vue";

const quizzesClient = new QuizzesClient();
const questionsClient = new QuestionsClient();
const coursesClient = new CoursesClient();
const chaptersClient = new ChaptersClient();
const resultsClient = new ResultsClient();
const studentsClient = new StudentsClient();

const route = useRoute();

const props = defineProps({
  curso: String,
  capitulo: String,
  quiz: String
});

// Quiz state
const quiz = ref(null);
const preguntas = ref([]);
const respuestas = ref({});
const quizSubmitted = ref(false);
const loading = ref(true);
const error = ref(null);
const forbidden = ref(false);  

// Course state
const course = ref(null);
const currentChapter = ref(null);
const backendScore = ref(0);
const notaMinima = ref(null); // example, could come from props or API

const launchConfetti = () => {
  confetti({
    particleCount: 700,
    spread: 150,
    origin: { y: 0.9 }
  });
};

// Fetch all data
const fetchData = async () => {
  quizSubmitted.value = false;
  try {
    loading.value = true;

    // Fetch everything in parallel
    const [quizData, questions, courseData, chapterData, ] = await Promise.all([
      quizzesClient.getQuizById(props.quiz),
      questionsClient.getQuestionsByQuizId(props.quiz),
      coursesClient.getCourseById(props.curso),
      chaptersClient.getChapterById(props.capitulo),,
    ]);

    // Assign state in one batch → fewer re-renders
    quiz.value = quizData;    
    course.value = courseData;
    currentChapter.value = chapterData;


    // Process questions with options & student answers
    preguntas.value = questions.map((question, idx) => {
      if (question.respuesta_estudiante_id !== null) {
        respuestas.value[idx] = question.respuesta_estudiante_id;
        quizSubmitted.value = true;
      }
      return {
        ...question,
        opcionCorrectaId: question.respuesta_correcta_id
      };
    });

    // If already submitted, get score
    if (quizSubmitted.value) {
      try {
        const profileResponse = await studentsClient.getProfile();
        const currentCourseProfile = profileResponse.find(p => p.curso_id === course.value.id);

        if (currentCourseProfile) {
          const quizResult = currentCourseProfile.quices.find(q => q.quizid === quizData.id);
          if (quizResult) {
            backendScore.value = quizResult.calificacion;
          }
        }

      } catch (err) {
        console.error("Error getting profile:", err);
      }
    }

  } catch (err) {
    if (err.message.includes("403")) {
      forbidden.value = true;
    } else {
      error.value = "Error al cargar los datos. Por favor intenta nuevamente.";
    }
    console.error("Error loading data:", err);
  } finally {
    loading.value = false;
  }
};

const score = computed(() => {
  return preguntas.value.reduce((total, pregunta, index) => {
    return total + (respuestas.value[index] === pregunta.opcionCorrectaId ? 1 : 0);
  }, 0);
});

const allQuestionsAnswered = computed(() => {
  return preguntas.value.length === Object.keys(respuestas.value).length;
});

const submitQuiz = async () => {
  if (!allQuestionsAnswered.value) {
    await Swal.fire({
      icon: 'info',
      text: 'Por favor responde todas las preguntas antes de enviar.',
      confirmButtonText: 'Entendido'
    });
    return;
  }

  // Confirmation before submit
  const { isConfirmed } = await Swal.fire({
    title: '¿Estás seguro?',
    text: 'Una vez enviado, no podrás cambiar tus respuestas.',
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Sí, enviar',
    cancelButtonText: 'Cancelar'
  });

  if (!isConfirmed) return; // User cancelled

  try {
    loading.value = true;

    // Prepare the submission data
    const submissionDto = {
      respuestas: Object.entries(respuestas.value).reduce((acc, [questionIndex, optionId]) => {
        const question = preguntas.value[questionIndex];
        acc[question.id] = optionId;
        return acc;
      }, {})
    };    
    
    await studentsClient.submitQuizAnswers(submissionDto).catch(e => {
      if (!e.message.includes('JSON')) throw e;    
    });    

    // Get the score from the backend
    const scoreResponse = await resultsClient.submitQuiz({
      quiz_id: quiz.value.id
    });    
    
    backendScore.value = scoreResponse;

    // Show success message
    await Swal.fire({
      icon: 'success',
      html: `
        <p>Tu calificación es: <strong>${backendScore.value}</strong></p>
        <p class="text-muted small">Respuestas correctas: ${score.value}/${preguntas.value.length}</p>
      `,
      confirmButtonText: 'OK'
    });

    quizSubmitted.value = true;

    Object.entries(respuestas.value).forEach(([index, optionId]) => {
      preguntas.value[index].respuesta_estudiante_id = optionId;
    });

    if (backendScore.value < notaMinima.value) {
      // allow retry
      Swal.fire({
        icon: "error",
        title: "Reprobado",
        text: `Necesitas mínimo ${quiz.nota_minima}. Puedes intentarlo de nuevo.`,
      });

      quizSubmitted.value = false; // reopen quiz
    }
    
    if (backendScore.value === 100) {
      launchConfetti();
    }

  } catch (err) {
    if (err.message.includes("401")) {
      await Swal.fire({
        icon: 'warning',
        title: 'Acceso restringido',
        text: 'Necesitas iniciar sesión para realizar este quiz',
        confirmButtonText: 'Entendido'
      });
    } else if (err.message.includes("400")) {
      await Swal.fire({
        icon: 'info',
        text: 'Ya enviaste este quiz anteriormente',
        confirmButtonText: 'OK'
      });

      // Try to get previous results
      try {
        const scoreResponse = await resultsClient.submitQuiz({
          quiz_id: quiz.value.id
        })
        
        backendScore.value = scoreResponse;
        quizSubmitted.value = true;
      } catch (scoreErr) {
        console.error("Error getting score:", scoreErr);
      }
    } else {
      console.error("Error submitting quiz:", err);
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Hubo un problema al enviar tu quiz',
        confirmButtonText: 'OK'
      });
    }
  } finally {
    loading.value = false;
  }
};

const resetQuiz = () => {
  respuestas.value = {};
  preguntas.value.forEach((p) => {
    p.respuesta_estudiante_id = null;
  });
  quizSubmitted.value = false;
  backendScore.value = null;
};

onMounted(() => {
  if (props.quiz) {
    fetchData();
  } else {
    error.value = "No se encontró el ID del quiz";
    loading.value = false;
  }
});

watch(
  () => route.params,
  (newParams) => {
    if (newParams.curso) {
      fetchData();
    }
  },
  { immediate: true }
);
</script>

<template>
  <div class="p-2 p-lg-4 container-fluid">
    <div v-if="loading" class="text-center py-10 my-10">
      <div class="spinner-border" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <div v-else-if="error" class="alert alert-danger">
      {{ error }}
    </div>

    <div v-else class="card">
      <!-- Card Header with Course Info -->
      <div class="card-header py-3 bg-white">
        <div class="d-flex justify-content-between align-items-center">
          <router-link :to="`/cursos/${route.params.curso}`" class="custom-hover-underline text-decoration-none text-reset">
            <h6 class="mb-0">
              <i class="fas fa-arrow-alt-circle-left fa-lg me-2"></i>
              {{ course?.titulo }}
            </h6>
          </router-link>
          
        </div>

        <div v-if="currentChapter" class="d-flex justify-content-between align-items-center">
          <p class="text-secondary font-weight-bold text-xs mt-0 mb-0">
            Capítulo {{ currentChapter.orden }}: {{ currentChapter.titulo }}
          </p>
        </div>
      </div>

      <div class="card-body">
        <div class="row">
          <!-- Main Quiz Content -->           
          <div class="col-xl-8 col-lg-7">
            <div v-if="forbidden" class="card border-warning">
              <div class="card-body text-center py-5">
                <div class="lock-icon mb-3">
                  <i class="fas fa-lock fa-4x text-primary"></i>
                </div>
                <h4 class="card-title fw-bold text-primary mb-3">Quiz Bloqueado</h4>
                <p class="card-text text-muted mb-4">                
                  <template>
                    Debes completar este quiz para continuar con el siguiente contenido.
                  </template>
                </p>
                
                <div class="requirements-card bg-light rounded p-3 mx-auto" style="max-width: 400px;">
                  <h6 class="fw-semibold mb-3">Requisitos para desbloquear:</h6>
                  <ul class="list-unstyled text-start">
                    <li class="mb-2">
                      <i class="fas fa-check-circle text-success me-2"></i>
                      Completar los contenido previo
                    </li>                               
                  </ul>
                </div>           
              </div>
            </div>
            <div v-else class="card p-4">
              <h4 class="mb-3">{{ quiz?.titulo || 'Quiz' }}</h4>
              <div class="lesson-descripcion mb-3 px-2 px-md-3">
                <h6 class="fw-bold">Instrucciones:</h6>
                <div v-if="quiz.instrucciones" v-html="quiz.instrucciones"></div>
                <div v-else>                      
                  <p class="text-muted py-2">No hay instrucciones para este quiz.</p>
                </div>
              </div>

              <hr class="mt-0 horizontal dark mb-3" />

              <div v-for="(pregunta, index) in preguntas" :key="pregunta.id" class="mb-2">
                <div class="d-flex align-items-center mb-2">
                  <h6 class="me-2 m-0 margin-bottom">{{ index + 1 }}.</h6>
                  <div class="question-text" v-html="pregunta.texto_pregunta"></div>
                </div>

                <div class="row mb-3">
                  <div v-for="opcion in pregunta.opciones" :key="opcion.id" class="col-md-6">
                    <div class="form-check d-flex align-items-center mb-3">
                      <input type="radio" class="form-check-input" :id="`pregunta-${pregunta.id}-opcion-${opcion.id}`"
                        :name="`pregunta-${pregunta.id}`" :value="opcion.id" v-model="respuestas[index]"
                        :disabled="quizSubmitted" :class="{
                          'is-valid': quizSubmitted && opcion.id === pregunta.opcionCorrectaId,
                          'is-invalid': quizSubmitted && opcion.id === pregunta.respuesta_estudiante_id && !opcion.es_correcta
                        }" />
                      <label class="form-check-label ms-2 mb-0 d-flex align-items-center text-muted"
                        :for="`pregunta-${pregunta.id}-opcion-${opcion.id}`" :class="{
                          'text-success': quizSubmitted && opcion.es_correcta,
                          'text-danger': quizSubmitted && opcion.id === pregunta.respuesta_estudiante_id && !opcion.es_correcta
                        }">
                        {{ opcion.texto }}
                        <span v-if="quizSubmitted" class="ms-2">
                          <span v-if="opcion.es_correcta">✅</span>
                          <span v-else-if="opcion.id === pregunta.respuesta_estudiante_id">❌</span>
                        </span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <hr class="horizontal dark mt-0 mb-4" />

              <button v-if="!quizSubmitted" @click="submitQuiz" class="btn btn-dark w-100">
                Enviar Respuestas
              </button>
              
              <div v-if="quizSubmitted" class="mt-4 text-center">
                <p>Calificación: <strong>{{ backendScore }}</strong></p>
                <h5>Respuestas correctas: {{ score }} / {{ preguntas.length }}</h5>   
              </div>
              <div v-if="quiz.nota_minima" class="text-center">              
                <p class="mt-3 text-sm">Nota mínima para aprobar:
                  <strong>{{ quiz.nota_minima }}</strong></p>
              </div>
              <div v-if="quizSubmitted && backendScore < quiz.nota_minima" class="mt-4 text-center">
                <button @click="resetQuiz" class="btn btn-warning">Reintentar Quiz</button>
              </div>
                
            </div>
          </div>

          <!-- Sidebar Navigation -->
          <div class="col-xl-4 col-lg-5">
             <div class="position-sticky top-10">
                <chapter-page :chapter-id="route.params.capitulo" :course-id="route.params.curso" />      
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.container-fluid {
  max-width: 1400px;
}

.question {
  text-align: start;
}

.question-text p{
  display: inline-block;
   margin-bottom: 0;
}


/* For content without any wrapping elements */
.question-text:not(:has(> *)) {
  display: inline;
  margin-bottom: 0;
  line-height: 1.5;
}

.accordion-item {
  border-bottom: 1px solid #dee2e6;
}

.accordion-button:not(.collapsed) {
  background-color: #f8f9fa;
}

.card-header {
  border-bottom: 1px solid rgba(0, 0, 0, 0.125);
}

.bg-light {
  background-color: rgba(var(--bs-primary-rgb), 0.1) !important;
}

.lesson-descripcion {
  background-color: #f8f9fa;
  border-left: 4px solid #0dfd69;
  padding: 0.4rem 1rem;
  border-radius: 0.25rem;
}

.is-valid {
  border-color: #28a745 !important;
}

.is-invalid {
  border-color: #dc3545 !important;
}

.text-success {
  color: #28a745 !important;
  font-weight: bold;
}

.text-danger {
  color: #dc3545 !important;
  font-weight: bold;
}

.form-check-input:disabled {
  opacity: 1;
}

.custom-hover-underline:hover h6 {
  text-decoration: underline;
}

.margin-bottom{
  margin-bottom: 1rem !important
}
</style>